/**
 * AJWalker — renders AJ the Coastal Fox as a transient three.js overlay. Two modes:
 *
 *  • WALK  (`/aj`)        — drops him on the bottom edge of the screen (that edge = the ground plane,
 *                           via an orthographic side camera) and walks him across, then tears down.
 *  • DEBUG (`/aj debug`)  — drops him CENTERED and enlarged, walking in place, with orbit controls
 *                           (drag to rotate, scroll to zoom). For inspecting the rig/arms from any
 *                           angle. Esc closes. This is a dev aid, not user-facing.
 *
 * Trigger: the `aj-walk` CustomEvent (from the secret /aj command); `detail.debug` selects the mode.
 * three.js is imported lazily so it stays out of the normal bundle. AJ himself is the reusable rig
 * from three/aj/ (GLB model if present, else the procedural fallback) — this component only places,
 * frames, lights, and moves him.
 *
 * FULLY OFF (2026-07, Matt's request): EASTER_EGG_ENABLED is false, which skips both trigger effects
 * entirely — no random walk-bys, and typing /aj silently no-ops (nothing is listening for the event).
 * Flip EASTER_EGG_ENABLED back to true to restore it.
 */

import { useEffect, useRef, useState } from 'react'

// Master on/off switch — set true to bring the fox back. While false, NEITHER trigger works: no
// random walk-bys and the secret /aj command silently no-ops (nothing is listening for its event).
const EASTER_EGG_ENABLED = false

// Rare random appearances for everyone logged in — same cadence as the soccer egg. AJ and the ball
// are mutually exclusive: each sets its own window flag (__ajWalkerActive / __ajSoccerActive) and
// checks the other's before a random spawn, so they never show at the same time.
const RANDOM_SPAWN_ENABLED = true
const CHECK_EVERY_MS = 60 * 1000
const SPAWN_CHANCE   = 0.002  // ~0.12/hr → about once a day per person; rare on purpose

const STRIP_PX = 300        // height of the bottom strip the WALK scene renders into
const STRIDE    = 1.25      // world units travelled per stride cycle (keeps feet from skating)
const WALK_CPS  = 1.6       // stride cycles per second (procedural rig)
const VX        = STRIDE * WALK_CPS   // procedural travel speed (world units / sec)

// GLB model: travel speed is locked to the baked Walking clip so his planted foot stays put.
const MODEL_TIMESCALE = 0.95   // clip playback rate (1 = native ~1.03s/cycle) — dialed in
const MODEL_PLANT_VX  = 1.14   // world units/sec that plants the feet at native (timescale 1) speed

// Shared look: filmic tone mapping + lights. Values dialed in via the /aj debug panel. Kept identical
// across both modes so he looks the same whether walking or being inspected.
const EXPOSURE = 1.48
// Key light orbits the character on a circle (the "Light angle" dial sets its azimuth).
// R = hypot of the key's x/z, H = its height. Baked azimuth = 44° → key at (4.68, 5, 4.52).
const KEY_R = 6.5
const KEY_H = 5
function addLights(THREE, scene) {
  // He's lit normally (emissive self-illumination killed in AJModel), so these carry his whole look.
  // Values tuned via the dial panel. NOTE: the fill is aimed behind him (lights the away-from-camera
  // side), so that dial does little — re-aim it to the front if you want it to matter.
  const ambient = new THREE.AmbientLight(0xffffff, 3.18); scene.add(ambient)
  const key = new THREE.DirectionalLight(0xfff1e0, 4.89); key.position.set(4.68, 5, 4.52); scene.add(key)
  const fill = new THREE.DirectionalLight(0xcfe0ff, 3.50); fill.position.set(-5, 2, -4); scene.add(fill)
  return { ambient, key, fill }
}

// Live-tunable defaults for the /aj debug dial panel — these MUST match the hard-coded values in
// addLights / AJModel so the dials start where the real look is. The point of the panel is to find
// good numbers by eye and feed them back; whatever you settle on becomes the new constants.
const DEBUG_DEFAULTS = {
  exposure: EXPOSURE, ambient: 3.18, key: 4.89, fill: 3.50, lightDir: 44,
  emissive: 0.05, specular: 0.90, armSwing: 0.29, armOut: 0.27,
  tailSwing: 0.89, tailSpeed: 4.80, timescale: MODEL_TIMESCALE, phaseFlip: 0,
}
// Ranges widened past the prior maxes (exposure/ambient/key/fill were pegged) so there's headroom.
const DEBUG_DIALS = [
  { k: 'exposure',  label: 'Exposure',    min: 0.4, max: 4.0, step: 0.01 },
  { k: 'ambient',   label: 'Ambient',     min: 0,   max: 3.5, step: 0.01 },
  { k: 'key',       label: 'Key light',   min: 0,   max: 7,   step: 0.01 },
  { k: 'fill',      label: 'Fill light',  min: 0,   max: 3.5, step: 0.01 },
  { k: 'lightDir',  label: 'Light angle', min: 0,   max: 360, step: 1 },
  { k: 'emissive',  label: 'Emissive',    min: 0,   max: 1,   step: 0.01 },
  { k: 'specular',  label: 'Specular',    min: 0,   max: 1.5, step: 0.01 },
  { k: 'armSwing',  label: 'Arm swing',   min: 0,   max: 1.2, step: 0.01 },
  { k: 'armOut',    label: 'Arm out',     min: 0,   max: 0.8, step: 0.01 },
  { k: 'tailSwing', label: 'Tail swing',  min: 0,   max: 1.5, step: 0.01 },
  { k: 'tailSpeed', label: 'Tail speed',  min: 0,   max: 8,   step: 0.1 },
  { k: 'timescale', label: 'Walk speed',  min: 0.3, max: 2.5, step: 0.05 },
  { k: 'phaseFlip', label: 'Arm flip',    min: 0,   max: 1,   step: 1 },
]

// A soft radial-gradient contact shadow as a canvas texture.
function makeShadowTexture(THREE) {
  const c = document.createElement('canvas')
  c.width = c.height = 128
  const g = c.getContext('2d')
  const grd = g.createRadialGradient(64, 64, 4, 64, 64, 62)
  grd.addColorStop(0, 'rgba(20,28,30,0.5)')
  grd.addColorStop(0.6, 'rgba(20,28,30,0.22)')
  grd.addColorStop(1, 'rgba(20,28,30,0)')
  g.fillStyle = grd
  g.fillRect(0, 0, 128, 128)
  const tex = new THREE.CanvasTexture(c)
  tex.minFilter = THREE.LinearFilter
  return tex
}

export default function AJWalker() {
  const [running, setRunning] = useState(false)
  const mountRef = useRef(null)
  const runningRef = useRef(false)
  const debugRef = useRef(false)
  const ctlRef = useRef(null)                 // { renderer, lights, aj } — live targets for the dials
  const [params, setParams] = useState(DEBUG_DEFAULTS)
  const [copied, setCopied] = useState(false)

  // Apply one dial value to the live three objects.
  function applyParam(k, v) {
    const c = ctlRef.current
    if (!c) return
    if (k === 'exposure') c.renderer.toneMappingExposure = v
    else if (k === 'ambient') c.lights.ambient.intensity = v
    else if (k === 'key') c.lights.key.intensity = v
    else if (k === 'fill') c.lights.fill.intensity = v
    else if (k === 'lightDir') { const az = v * Math.PI / 180; c.lights.key.position.set(KEY_R * Math.cos(az), KEY_H, KEY_R * Math.sin(az)) }
    else if (k === 'emissive' && c.aj.setEmissive) c.aj.setEmissive(v)
    else if (k === 'specular' && c.aj.setSpecular) c.aj.setSpecular(v)
    else if (k === 'armSwing' && c.aj.setArmParams) c.aj.setArmParams({ swing: v })
    else if (k === 'armOut' && c.aj.setArmParams) c.aj.setArmParams({ out: v })
    else if (k === 'phaseFlip' && c.aj.setArmParams) c.aj.setArmParams({ phaseOff: v ? Math.PI : 0 })
    else if (k === 'tailSwing' && c.aj.setTailParams) c.aj.setTailParams({ swing: v })
    else if (k === 'tailSpeed' && c.aj.setTailParams) c.aj.setTailParams({ speed: v })
    else if (k === 'timescale' && c.aj.setTimeScale) c.aj.setTimeScale(v)
  }
  function onDial(k, v) { setParams(p => ({ ...p, [k]: v })); applyParam(k, v) }
  function copyValues() {
    const txt = DEBUG_DIALS.map(d => `${d.k} ${Number(params[d.k]).toFixed(2)}`).join(' · ')
    if (navigator.clipboard) navigator.clipboard.writeText(txt).catch(() => {})
    setCopied(true); setTimeout(() => setCopied(false), 1200)
  }

  // Secret trigger: the /aj command dispatches `aj-walk` (detail.debug → inspect mode).
  useEffect(() => {
    if (!EASTER_EGG_ENABLED) return
    const onWalk = (e) => {
      if (runningRef.current) return
      runningRef.current = true
      debugRef.current = !!(e.detail && e.detail.debug)
      setRunning(true)
    }
    window.addEventListener('aj-walk', onWalk)
    return () => window.removeEventListener('aj-walk', onWalk)
  }, [])

  // Random walk-by (everyone). Visible-tab only; a roll while hidden is queued for return. Skips if
  // AJ is already running or the soccer ball is on screen.
  useEffect(() => {
    if (!EASTER_EGG_ENABLED || !RANDOM_SPAWN_ENABLED) return
    let pending = false
    const tryStart = () => {
      if (runningRef.current || window.__ajSoccerActive) return
      debugRef.current = false
      runningRef.current = true
      setRunning(true)
    }
    const interval = setInterval(() => {
      if (Math.random() >= SPAWN_CHANCE) return
      if (document.visibilityState === 'visible') tryStart()
      else pending = true
    }, CHECK_EVERY_MS)
    const reveal = () => { if (pending && document.visibilityState === 'visible') { pending = false; tryStart() } }
    document.addEventListener('visibilitychange', reveal)
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', reveal) }
  }, [])

  useEffect(() => {
    if (!running) return
    const debug = debugRef.current
    window.__ajWalkerActive = true       // tell the soccer egg to stand down while AJ is on screen
    let cancelled = false, raf = 0, cleanup = () => {}

    // The authored GLB is the only fox we show. If it fails to load (e.g. a transient fetch hiccup on
    // the ~12MB file) we return null and skip this appearance — better than flashing the primitive
    // procedural fallback at everyone. The next random roll just tries again.
    async function makeAJ(THREE) {
      try {
        const { loadAJModel } = await import('../three/aj/AJModel.js')
        return await loadAJModel(THREE, '/aj/aj-fox.glb')
      } catch (e) {
        console.warn('[AJWalker] AJ model failed to load; skipping this walk-by', e)
        return null
      }
    }

    import('three').then(async THREE => {
      if (cancelled || !mountRef.current) return
      const host = mountRef.current

      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      renderer.setClearColor(0x000000, 0)
      renderer.toneMapping = THREE.ACESFilmicToneMapping
      renderer.toneMappingExposure = EXPOSURE
      const cv = renderer.domElement
      cv.style.position = 'fixed'; cv.style.display = 'block'
      if (debug) { cv.style.left = '0'; cv.style.top = '0' }
      else       { cv.style.left = '0'; cv.style.bottom = '0' }
      host.appendChild(cv)

      const scene = new THREE.Scene()
      const lights = addLights(THREE, scene)

      const aj = await makeAJ(THREE)
      if (cancelled || !aj) {
        // aborted, or the model didn't load — tear down what we already created and bail.
        if (aj && aj.dispose) aj.dispose()
        renderer.dispose()
        if (cv.parentNode) cv.parentNode.removeChild(cv)
        window.__ajWalkerActive = false
        runningRef.current = false
        if (!cancelled) setRunning(false)
        return
      }
      aj.play('walk')
      if (aj.setTimeScale) aj.setTimeScale(MODEL_TIMESCALE)
      if (aj.setWalkSpeed) aj.setWalkSpeed(WALK_CPS)
      scene.add(aj.group)

      // Contact shadow (shared).
      const shadowTex = makeShadowTexture(THREE)
      const shadowMat = new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false })
      const shadow = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 0.9), shadowMat)
      shadow.rotation.x = -Math.PI / 2
      shadow.position.y = 0.01
      scene.add(shadow)

      /* ── DEBUG: centered, in place, orbitable ────────────────────────────────── */
      if (debug) {
        aj.group.rotation.y = 0          // face the camera (+Z); orbit moves the camera, not him
        aj.group.position.set(0, 0, 0)
        shadow.position.set(0, 0.01, 0)

        const midY = aj.topY * 0.5
        const camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 1000)
        camera.position.set(0.8, midY + 0.4, 6.5)
        function setSize() {
          const w = window.innerWidth, h = window.innerHeight
          renderer.setSize(w, h, false); cv.style.width = w + 'px'; cv.style.height = h + 'px'
          camera.aspect = w / h; camera.updateProjectionMatrix()
        }
        setSize(); window.addEventListener('resize', setSize)

        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js')
        if (cancelled) { cleanup(); return }
        const controls = new OrbitControls(camera, cv)
        controls.target.set(0, midY, 0)
        controls.enablePan = false
        controls.enableDamping = true
        controls.dampingFactor = 0.08
        controls.minDistance = 2.5
        controls.maxDistance = 20
        controls.update()

        const onKey = (e) => { if (e.key === 'Escape') cleanup() }
        window.addEventListener('keydown', onKey)

        // Expose the live targets to the dial panel + apply the current panel values once.
        ctlRef.current = { renderer, lights, aj }
        DEBUG_DIALS.forEach(d => applyParam(d.k, params[d.k]))

        let last = performance.now()
        function frame(now) {
          if (cancelled) return
          const dt = Math.min((now - last) / 1000, 0.05); last = now
          aj.update(dt)
          controls.update()
          renderer.render(scene, camera)
          raf = requestAnimationFrame(frame)
        }
        raf = requestAnimationFrame(frame)

        cleanup = () => {
          cancelAnimationFrame(raf)
          window.removeEventListener('resize', setSize)
          window.removeEventListener('keydown', onKey)
          controls.dispose()
          aj.dispose()
          shadow.geometry.dispose(); shadowMat.dispose(); shadowTex.dispose()
          renderer.dispose()
          if (cv.parentNode) cv.parentNode.removeChild(cv)
          ctlRef.current = null
          window.__ajWalkerActive = false
          runningRef.current = false
          if (!cancelled) setRunning(false)
        }
        return
      }

      /* ── WALK: bottom-edge ground plane, walk across right→left ──────────────── */
      aj.setFacing(-1)       // face/walk LEFT — a Y-axis yaw, not a mirror
      let travelVX = VX
      if (aj.isModel) travelVX = MODEL_PLANT_VX * MODEL_TIMESCALE

      const groundPad = 0.02         // world units shown below his feet — ~0 so he walks on the very
                                     // bottom edge of the window (y=0 maps to the canvas bottom)
      const topWorld  = aj.topY + 0.35
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 400)
      camera.position.set(0, 0, 120)
      let halfSpan = 8
      function setSize() {
        const w = window.innerWidth, h = STRIP_PX
        renderer.setSize(w, h, false); cv.style.width = w + 'px'; cv.style.height = h + 'px'
        const worldPerPx = (topWorld + groundPad) / h
        halfSpan = (worldPerPx * w) / 2
        camera.left = -halfSpan; camera.right = halfSpan
        camera.top = topWorld;   camera.bottom = -groundPad
        camera.updateProjectionMatrix()
      }
      setSize()
      window.addEventListener('resize', setSize)

      const margin = 1.4
      aj.group.position.set(halfSpan + margin, 0, 0)   // start off the right edge

      let last = performance.now()
      function frame(now) {
        if (cancelled) return
        const dt = Math.min((now - last) / 1000, 0.05); last = now

        aj.update(dt)
        aj.group.position.x -= travelVX * dt

        shadow.position.x = aj.group.position.x + 0.08
        if (aj.joints && aj.joints.hips) {
          const hipY = aj.joints.hips.getWorldPosition(new THREE.Vector3()).y
          const lift = Math.max(0, hipY - 1.0)
          const sc = 1 - lift * 1.6
          shadow.scale.set(sc, sc, sc)
          shadowMat.opacity = 0.9 - lift * 4
        }

        renderer.render(scene, camera)

        if (aj.group.position.x < -halfSpan - margin) { cleanup(); return }
        raf = requestAnimationFrame(frame)
      }
      raf = requestAnimationFrame(frame)

      cleanup = () => {
        cancelAnimationFrame(raf)
        window.removeEventListener('resize', setSize)
        aj.dispose()
        shadow.geometry.dispose(); shadowMat.dispose(); shadowTex.dispose()
        renderer.dispose()
        if (cv.parentNode) cv.parentNode.removeChild(cv)
        window.__ajWalkerActive = false
        runningRef.current = false
        if (!cancelled) setRunning(false)
      }
    }).catch(() => { window.__ajWalkerActive = false; runningRef.current = false; if (!cancelled) setRunning(false) })

    return () => { cancelled = true; window.__ajWalkerActive = false; cleanup() }
  }, [running])

  if (!running) return null
  const debug = debugRef.current
  return (
    <>
      {debug && (
        <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 8999, background: 'rgba(8,12,14,0.5)', pointerEvents: 'none' }} />
      )}
      <div
        ref={mountRef}
        aria-hidden="true"
        style={debug
          ? { position: 'fixed', inset: 0, zIndex: 9000, pointerEvents: 'auto' }
          : { position: 'fixed', left: 0, bottom: 0, width: '100vw', height: STRIP_PX, zIndex: 9000, pointerEvents: 'none' }}
      />
      {debug && (
        <div style={{
          position: 'fixed', bottom: 22, left: 0, right: 0, textAlign: 'center', zIndex: 9001,
          pointerEvents: 'none', fontFamily: '"DM Mono", monospace', fontSize: 12,
          letterSpacing: '0.04em', color: 'rgba(255,255,255,0.82)',
        }}>
          drag to rotate · scroll to zoom · esc to close
        </div>
      )}
      {debug && (
        <div style={{
          position: 'fixed', top: 16, left: 16, zIndex: 9002, pointerEvents: 'auto',
          width: 250, padding: '12px 14px', borderRadius: 10,
          background: 'rgba(12,18,20,0.86)', color: '#eaf0f1',
          fontFamily: '"DM Mono", monospace', fontSize: 11.5,
          boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
        }}>
          <div style={{ fontWeight: 600, letterSpacing: '0.06em', marginBottom: 8, opacity: 0.9 }}>AJ · DEBUG DIALS</div>
          {DEBUG_DIALS.map(d => (
            <label key={d.k} style={{ display: 'block', marginBottom: 7 }}>
              <span style={{ display: 'flex', justifyContent: 'space-between', opacity: 0.85 }}>
                <span>{d.label}</span>
                <span>{Number(params[d.k]).toFixed(d.step >= 1 ? 0 : 2)}</span>
              </span>
              <input
                type="range" min={d.min} max={d.max} step={d.step} value={params[d.k]}
                onChange={e => onDial(d.k, parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#b95e2f', cursor: 'pointer' }}
              />
            </label>
          ))}
          <button
            onClick={copyValues}
            style={{
              marginTop: 4, width: '100%', padding: '6px 0', borderRadius: 6, cursor: 'pointer',
              border: '0.5px solid rgba(255,255,255,0.3)', background: copied ? '#2f6d3a' : 'rgba(255,255,255,0.1)',
              color: '#eaf0f1', fontFamily: 'inherit', fontSize: 11.5,
            }}
          >
            {copied ? 'copied ✓' : 'copy values'}
          </button>
        </div>
      )}
    </>
  )
}
